import { useEffect, useState, useCallback, useMemo, useRef, createContext, useContext, Fragment } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/client/lib/auth";
import { api } from "@/client/lib/api";
import { LockIcon, FileTextIcon } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { Dialog, DialogTrigger, DialogPopup, DialogHeader, DialogTitle } from "@/client/components/ui/dialog";
import { PortalNav } from "./PortalNav";
import { PortalHeader } from "./PortalHeader";
import { SaveToast } from "./SaveToast";
import { stepIconKind, StepCircle } from "./stepper-icon";
import { usePortalBootstrap, prefetchOverview, usePortalPreflight, portalQueries, readPortalCache, writePortalCache, flowFlags } from "@/client/lib/portal";
import { invalidateSelectionDerived } from "@/client/lib/query";
import { usePreviewMode } from "@/client/lib/preview";
import {
  PROGRAMMA_SECTION_CODES,
  isProgrammaSectionCode,
  isCompleted,
  isUnavailable,
  deriveSectionStatus,
  sectionToApiCode,
  type ProgrammaSectionCode,
  type SectionStatus,
  type DeliveryState,
} from "./portal-flow";

type ProgrammaSectionMeta = {
  code: ProgrammaSectionCode;
  title: string;
  summary: string;
  status: SectionStatus;
  deliveryState?: DeliveryState;
  deadline?: string;
  // Per-group rollup from the server (vast_keuze / technohub only), so the stepper's INACTIVE
  // parent icon is correct without subNavItems. allGroupsResolved -> check, anyGroupStarted (but
  // not all) -> dot, neither -> ring. While the section is ACTIVE, subNavItems take precedence.
  allGroupsResolved?: boolean;
  anyGroupStarted?: boolean;
};

type SchoolInfo = {
  schoolId: number;
  schoolName: string;
  shortName: string | null;
};

type SaveState = "idle" | "saving" | "saved" | "error";

type ProgrammaContextType = {
  schoolId: number;
  schoolInfo: SchoolInfo | null;
  sections: ProgrammaSectionMeta[];
  activeSection: ProgrammaSectionCode | null;
  saveState: SaveState;
  lastSavedAt: string | null;
  setSaveState: (state: SaveState) => void;
  setLastSavedAt: (time: string) => void;
  refreshSections: () => Promise<void>;
  completeSection: () => void;
  primaryAction: { label: string; onClick: () => void; isConfirm: boolean };
  // Per-group sub-nav: the active per-group section (vast_keuze/technohub) publishes its groups
  // here so the shell renders them nested in the left stepper, sharing the active group. `partial`
  // = the group is deels ingevuld (some decision made) but not yet fully resolved -> (o) dot.
  subNavItems: { id: number; label: string; resolved: boolean; partial: boolean }[];
  setSubNavItems: (items: { id: number; label: string; resolved: boolean; partial: boolean }[]) => void;
  activeSubId: number | null;
  setActiveSubId: (id: number | null) => void;
};

const ProgrammaContext = createContext<ProgrammaContextType | null>(null);

export function useProgramma() {
  const ctx = useContext(ProgrammaContext);
  if (!ctx) throw new Error("useProgramma must be used within ProgrammaShell");
  return ctx;
}

const SECTION_TITLES: Record<ProgrammaSectionCode, string> = {
  vast_keuze: "Vast- & keuzeprogramma",
  technohub: "TechnoHub",
  deskundigheidsbevordering: "Deskundigheidsbevordering",
  maatwerk: "Extra maatwerk",
  programma_controleren: "Controleren",
};

const SECTION_SHORT_TITLES: Record<ProgrammaSectionCode, string> = {
  vast_keuze: "Vast- & keuzeprogramma",
  technohub: "TechnoHub",
  deskundigheidsbevordering: "Deskundigheidsbevordering",
  maatwerk: "Extra maatwerk",
  programma_controleren: "Controleren",
};

/** Pure builder: derive the programma stepper from a /progress payload. Used by the async
 *  refresh and to seed initial state synchronously from the prefetched cache. */
function buildProgrammaSections(progress: any): ProgrammaSectionMeta[] {
  const steps: any[] = progress?.steps ?? [];
  const counts = progress?.counts ?? {};
  const rollup = progress?.programmaSectionResolution ?? {};
  const vastKeuzeRollup = rollup.vast_keuze as { allGroupsResolved?: boolean; anyGroupStarted?: boolean } | undefined;
  // Per-school opt-outs for the two SCHOOL-WIDE optional sections (deskundigheidsbevordering /
  // maatwerk). An opted-out section is "done" even without a selection: done = (opted out) OR
  // (>=1 selection). Without this an optional section never reaches done (nothing is required).
  const sectionOptOuts: string[] = progress?.sectionOptOuts ?? [];
  const deskundigheidOptedOut = sectionOptOuts.includes("deskundigheidsbevordering");
  const maatwerkOptedOut = sectionOptOuts.includes("maatwerk");
  const technohubOptedOut = sectionOptOuts.includes("technohub");
  return [
    (() => {
      const vastStep = steps.find((s: any) => s.sectionCode === "fixed_programme");
      const keuzeStep = steps.find((s: any) => s.sectionCode === "optional_programme");
      const total = (counts.vastSelections ?? 0) + (counts.keuzeSelections ?? 0);
      const vastStatus = deriveSectionStatus(vastStep, (counts.vastSelections ?? 0) > 0);
      const keuzeStatus = deriveSectionStatus(keuzeStep, (counts.keuzeSelections ?? 0) > 0);
      const status: SectionStatus =
        vastStatus === "not_open" && keuzeStatus === "not_open" ? "not_open"
          : vastStatus === "done" && keuzeStatus === "done" ? "done"
            : vastStatus !== "todo" || keuzeStatus !== "todo" ? "in_progress" : "todo";
      const vastDS = vastStep?.deliveryState as DeliveryState | undefined;
      const keuzeDS = keuzeStep?.deliveryState as DeliveryState | undefined;
      const deliveryState: DeliveryState | undefined =
        vastDS === "changed" || keuzeDS === "changed" ? "changed"
          : vastDS === "delivered" || keuzeDS === "delivered" ? "delivered"
            : (vastDS ?? keuzeDS);
      return {
        code: "vast_keuze" as ProgrammaSectionCode,
        title: SECTION_TITLES.vast_keuze,
        summary: total > 0 ? `${total} programma${total !== 1 ? "'s" : ""} gekozen` : "Nog niet ingevuld",
        status,
        deliveryState,
        deadline: keuzeStep?.closesAt ?? vastStep?.closesAt ?? undefined,
        allGroupsResolved: vastKeuzeRollup?.allGroupsResolved,
        anyGroupStarted: vastKeuzeRollup?.anyGroupStarted,
      };
    })(),
    (() => {
      const hasSelections = (counts.technohubSelections ?? 0) > 0;
      // TechnoHub is now a single workshop-centric OPTIONAL page (no per-group sub-nav), so it
      // mirrors Deskundigheidsbevordering / Extra maatwerk: DONE (blue/green check) as soon as the
      // school EITHER opts out ("we doen dit jaar niet mee") OR chooses >=1 workshop; "not started"
      // (empty ring) when neither. Never "in_progress" — so a single workshop already reads as a
      // check rather than lingering on a "bezig" dot until the whole flow is aangeleverd.
      const status: SectionStatus = technohubOptedOut || hasSelections ? "done" : "todo";
      return {
        code: "technohub" as ProgrammaSectionCode,
        title: SECTION_TITLES.technohub,
        summary: technohubOptedOut ? "Geen workshops van TechnoHub dit jaar" : hasSelections ? `${counts.technohubSelections} workshop${counts.technohubSelections !== 1 ? "s" : ""} gekozen` : "Optioneel",
        status,
        deliveryState: steps.find((s: any) => s.sectionCode === "technohub_programme")?.deliveryState ?? undefined,
        deadline: steps.find((s: any) => s.sectionCode === "technohub_programme")?.closesAt ?? undefined,
      };
    })(),
    (() => {
      const hasSelections = (counts.deskundigheidsbevorderingSelections ?? 0) > 0;
      // School-wide OPTIONAL section: there is no meaningful "bezig" middle state. The section
      // resolves to DONE (blue/green check) as soon as the school EITHER opts out ("we doen dit
      // jaar niet mee") OR indicates interest in >=1 training; it stays "not started" (empty ring)
      // when neither. Never "in_progress" — so a single interesse already reads as a check rather
      // than lingering on a "bezig" dot until the whole flow is aangeleverd.
      const status: SectionStatus = deskundigheidOptedOut || hasSelections ? "done" : "todo";
      return {
        code: "deskundigheidsbevordering" as ProgrammaSectionCode,
        title: SECTION_TITLES.deskundigheidsbevordering,
        summary: deskundigheidOptedOut ? "Geen deskundigheidsbevordering dit jaar" : hasSelections ? `${counts.deskundigheidsbevorderingSelections} ${counts.deskundigheidsbevorderingSelections === 1 ? "training" : "trainingen"} gekozen` : "Optioneel",
        status,
        deliveryState: steps.find((s: any) => s.sectionCode === "deskundigheidsbevordering_programme")?.deliveryState ?? undefined,
        deadline: steps.find((s: any) => s.sectionCode === "deskundigheidsbevordering_programme")?.closesAt ?? undefined,
      };
    })(),
    (() => {
      const hasSelections = (counts.maatwerkSelections ?? 0) > 0;
      // School-wide OPTIONAL section: no "bezig" middle state. DONE (check) as soon as the school
      // EITHER opts out OR indicates interest in >=1 entry; "not started" (empty ring) when neither.
      // Mirrors deskundigheidsbevordering above — never "in_progress".
      const status: SectionStatus = maatwerkOptedOut || hasSelections ? "done" : "todo";
      return {
        code: "maatwerk" as ProgrammaSectionCode,
        title: SECTION_TITLES.maatwerk,
        summary: maatwerkOptedOut ? "Geen extra maatwerk dit jaar" : hasSelections ? `${counts.maatwerkSelections} aanvra${counts.maatwerkSelections !== 1 ? "gen" : "ag"} doorgegeven` : "Optioneel",
        status,
        deliveryState: steps.find((s: any) => s.sectionCode === "maatwerk_programme")?.deliveryState ?? undefined,
        deadline: steps.find((s: any) => s.sectionCode === "maatwerk_programme")?.closesAt ?? undefined,
      };
    })(),
    {
      code: "programma_controleren",
      title: SECTION_TITLES.programma_controleren,
      summary: (() => {
        const vastDone = deriveSectionStatus(steps.find((s: any) => s.sectionCode === "fixed_programme"), (counts.vastSelections ?? 0) > 0);
        const keuzeDone = deriveSectionStatus(steps.find((s: any) => s.sectionCode === "optional_programme"), (counts.keuzeSelections ?? 0) > 0);
        return vastDone === "done" && keuzeDone === "done" ? "Klaar om te controleren" : "Rond eerst de andere stappen af";
      })(),
      status: deriveSectionStatus(steps.find((s: any) => s.sectionCode === "programme_review"), false),
      deadline: steps.find((s: any) => s.sectionCode === "programme_review")?.closesAt ?? undefined,
    },
  ];
}

function canOpenProgrammaSection(sections: ProgrammaSectionMeta[], code: ProgrammaSectionCode): boolean {
  const index = sections.findIndex((s) => s.code === code);
  if (index === -1) return false;
  const section = sections[index]!;
  if (isUnavailable(section.status)) return false;
  // Both sections are always accessible — no strict ordering needed
  return true;
}

function getNextProgrammaSection(sections: ProgrammaSectionMeta[]): ProgrammaSectionCode | null {
  for (const s of sections) {
    if (isUnavailable(s.status)) continue;
    if (!isCompleted(s.status)) return s.code as ProgrammaSectionCode;
  }
  return sections.at(-1)?.code as ProgrammaSectionCode ?? null;
}

export function ProgrammaShell({
  renderSection,
}: {
  renderSection: (code: ProgrammaSectionCode, schoolId: number) => React.ReactNode;
}) {
  const { schoolId: scIdParam, section: sectionParam } = useParams<{
    schoolId: string;
    section?: string;
  }>();
  const schoolId = Number(scIdParam);
  const requestedSection = isProgrammaSectionCode(sectionParam) ? sectionParam : null;

  const navigate = useNavigate();
  const location = useLocation();
  const isPreview = usePreviewMode();

  const { data: bootstrap } = usePortalBootstrap(schoolId);
  usePortalPreflight(schoolId);

  // Seed the stepper from the prefetched /progress cache for an instant first paint.
  const cachedProgress = readPortalCache<any>(portalQueries.progress(schoolId).queryKey);

  // Per-GROUP programme delivery state (keyed by class_group_id), authoritative from /progress
  // (phase 1's content-fingerprint fix). Drives the per-group sub-nav badge so changing ONE group
  // only flags THAT group, not the whole section. Seeded from the prefetched cache for first paint.
  const [groupDelivery, setGroupDelivery] = useState<Record<string, DeliveryState>>(() => cachedProgress?.programmaGroupDelivery ?? {});
  // OPTIMISTIC "changed" hint. Editing always means changed (safe to assume), so on a save we flip
  // the edited group (and, via controlerenDelivery / the section badge below, the flow) to "changed"
  // IMMEDIATELY for instant feedback, then RECONCILE with the authoritative /progress + bootstrap
  // once they arrive (which may flip a group back to "delivered" if the edit reverted to the
  // submitted value). The set is the class_group_ids currently optimistically-changed; the boolean
  // tracks the flow.
  const [optimisticGroups, setOptimisticGroups] = useState<Set<number>>(() => new Set());
  const [optimisticFlowChanged, setOptimisticFlowChanged] = useState(false);
  // WHICH section the in-flight edit belongs to, so ONLY that section's badge reacts optimistically
  // (e.g. a TechnoHub edit must NOT flash "Gewijzigd" on Vast- & keuzeprogramma). Controleren is
  // flow-level and uses optimisticFlowChanged.
  const [optimisticSection, setOptimisticSection] = useState<ProgrammaSectionCode | null>(null);

  // Whole-flow programma delivery state for the Controleren step's icon (green=aangeleverd,
  // (o)=gewijzigd). The data steps carry their own per-section deliveryState.
  const progFlow = bootstrap ? flowFlags(bootstrap.flows.programmaselectie) : null;
  // delivered+unchanged -> "delivered" (green check on Controleren, after phase 1's fix
  // hasUnsubmittedChanges is correctly false post-revert); never-submitted -> "no_version";
  // pending changes -> "changed". The optimistic flag forces "changed" the instant a save starts
  // (but only when the flow WAS delivered, so we never downgrade a never_submitted node), then the
  // refreshed bootstrap reconciles it back to delivered if the edit reverted to the submitted value.
  const controlerenDelivery: DeliveryState =
    !progFlow?.everSubmitted ? "no_version"
      : progFlow.hasUnsubmittedChanges || optimisticFlowChanged ? "changed"
        : "delivered";
  const [subNavItems, setSubNavItems] = useState<{ id: number; label: string; resolved: boolean; partial: boolean }[]>([]);

  // The active per-group sub-nav id. In-memory only: it defaults to null and the active
  // section's own "default to the first group" effect seeds the first published group on mount.
  const [activeSubId, setActiveSubId] = useState<number | null>(null);

  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | null>(null);
  const [sections, setSections] = useState<ProgrammaSectionMeta[]>(() => (cachedProgress ? buildProgrammaSections(cachedProgress) : []));
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(() => !cachedProgress);
  const [loadError, setLoadError] = useState(false);

  const refreshSections = useCallback(async () => {
    try {
      setLoadError(false);
      const schoolsRes = await (api.api.school as any)["my-schools"].$get();
      if (schoolsRes.ok) {
        const data = await schoolsRes.json();
        const schools = (data as any).schools ?? [];
        const school = schools.find((s: any) => s.schoolId === schoolId);
        if (school) {
          setSchoolInfo({
            schoolId: school.schoolId,
            schoolName: school.shortName || school.schoolName,
            shortName: school.shortName,
          });
        }
      }

      const progressRes = await fetch(`/api/school/${schoolId}/progress`, { credentials: "include" });
      if (progressRes.ok) {
        const progress = await progressRes.json();
        writePortalCache(portalQueries.progress(schoolId).queryKey, progress);
        setSections(buildProgrammaSections(progress));
        // Authoritative per-group delivery from the server (phase 1). Adopting it RECONCILES the
        // optimistic "changed" hint: a group the server now reports as "delivered" (the edit
        // reverted to the submitted value) flips back, while a genuinely changed group stays
        // "changed". Clearing the optimistic set + flow flag here hands authority back to the server.
        setGroupDelivery(progress.programmaGroupDelivery ?? {});
        setOptimisticGroups(new Set());
        setOptimisticFlowChanged(false);
        setOptimisticSection(null);
      }
    } catch (e) {
      console.error("Failed to load programma data", e);
      setLoadError(true);
    }
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { refreshSections(); }, [refreshSections]);

  // Idle-prefetch the overview so returning to the Overzicht tab is instant.
  useEffect(() => {
    const t = setTimeout(() => prefetchOverview(schoolId), 800);
    return () => clearTimeout(t);
  }, [schoolId]);

  // After a save settles: ONE coalesced refresh. The section that owns the edit (VastKeuzeSection)
  // already reloads + re-caches its own /selections + /eligible per save, so here we only refresh
  // the DERIVED state: refreshSections() fetches /progress fresh (the stepper + per-group delivery,
  // reconciling the optimistic hint) and invalidateSelectionDerived re-fetches just the two OTHER
  // useQuery-observed derived reads — the bootstrap (Controleren delivery) and the overview. It no
  // longer invalidates /progress (refreshSections already owns the fresh copy), so there is no double
  // /progress work. We also do NOT call refreshSections() inside the section's saved() too, nor
  // invalidate the whole school subtree: that double refresh + 8-query invalidation on every click was
  // the cascade that froze the nav. The 250ms debounce coalesces a burst of saves into one refresh.
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveState === "saved") {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => { void refreshSections(); void invalidateSelectionDerived(schoolId); }, 250);
    }
    return undefined;
  }, [saveState, refreshSections, schoolId]);

  // Canonical section
  const activeSection = useMemo(() => {
    if (loading || sections.length === 0) return null;
    if (requestedSection && canOpenProgrammaSection(sections, requestedSection)) return requestedSection;
    return getNextProgrammaSection(sections);
  }, [loading, sections, requestedSection]);

  // OPTIMISTIC "changed" — instant status feedback. The moment a save starts (saveState ->
  // "saving"), flip the group being edited (the active sub-nav group, within the per-group
  // Vast- & keuze section) and the whole programmaselectie flow to "changed", so the per-group
  // badge and the Controleren node react immediately instead of waiting for the debounced
  // /progress + bootstrap roundtrip. This is only a HINT: editing always means changed, so it is
  // always safe; the authoritative server response (refreshSections clears the optimistic set,
  // the bootstrap refresh clears optimisticFlowChanged) then confirms or CORRECTS it (e.g. flips a
  // group back to "delivered" when the edit reverted to the submitted value). We deliberately do
  // NOT compute a client-side fingerprint here — the server stays the single source of truth.
  useEffect(() => {
    if (saveState !== "saving") return;
    setOptimisticFlowChanged(true);
    setOptimisticSection(activeSection);
    if (activeSection === "vast_keuze" && activeSubId != null) {
      setOptimisticGroups((prev) => (prev.has(activeSubId) ? prev : new Set(prev).add(activeSubId)));
    }
  }, [saveState, activeSection, activeSubId]);

  useEffect(() => {
    if (loading || !activeSection) return;
    const canonicalPath = `/school/${schoolId}/programmaselectie/${activeSection}`;
    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [loading, activeSection, schoolId, location.pathname, navigate]);

  const handleOpenSection = useCallback(
    (code: ProgrammaSectionCode) => {
      if (!canOpenProgrammaSection(sections, code)) return;
      navigate(`/school/${schoolId}/programmaselectie/${code}`);
    },
    [sections, navigate, schoolId],
  );

  const completeSection = useCallback(async () => {
    if (!activeSection) return;
    setSaveState("saving");
    const idx = PROGRAMMA_SECTION_CODES.indexOf(activeSection);
    const nextCode = idx < PROGRAMMA_SECTION_CODES.length - 1 ? PROGRAMMA_SECTION_CODES[idx + 1] : null;
    // The merged Vast- & keuzeprogramma section confirms both underlying windows.
    const apiCodes = activeSection === "vast_keuze" ? ["fixed_programme", "optional_programme"] : [sectionToApiCode(activeSection)];
    try {
      for (const ac of apiCodes) {
        const res = await fetch(`/api/school/${schoolId}/submit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ sectionCode: ac }),
        });
        if (!res.ok) { setSaveState("error"); return; }
      }
      await refreshSections();
      setSaveState("idle");
      if (nextCode) navigate(`/school/${schoolId}/programmaselectie/${nextCode}`);
    } catch {
      setSaveState("error");
    }
  }, [activeSection, schoolId, refreshSections, navigate]);

  // Primary button (the section's bottom button). Behaviour depends on the section:
  //  - Within a per-group section (Vast- & keuze, TechnoHub) the button walks the GROUPS: it
  //    advances to the NEXT GROUP ("Naar: <next group>"), and only on the LAST group does it
  //    advance to the NEXT SECTION ("Naar: <next section title>"). It no longer jumps straight to
  //    TechnoHub mid-groups. (No per-section "confirm" here; the real aanlevering is on Controleren.)
  //  - Other sections (Deskundigheidsbevordering, Extra maatwerk) just advance to the next section.
  // The Controleren step is the final confirm/aanlever step and renders its own button, so it does
  // not rely on this primaryAction.
  const primaryAction = useMemo(() => {
    const idx = activeSection ? PROGRAMMA_SECTION_CODES.indexOf(activeSection) : -1;
    const nextSection = idx >= 0 && idx < PROGRAMMA_SECTION_CODES.length - 1 ? PROGRAMMA_SECTION_CODES[idx + 1]! : null;
    const goToNextSection = () =>
      nextSection
        ? navigate(`/school/${schoolId}/programmaselectie/${nextSection}`)
        : navigate(`/school/${schoolId}/overzicht`);
    const nextSectionLabel = nextSection ? `Naar: ${SECTION_SHORT_TITLES[nextSection]}` : "Naar overzicht";

    // Only Vast- & keuzeprogramma still steps group-by-group. TechnoHub is now a single
    // workshop-centric page (no per-group sub-nav), so it falls through to "advance to next".
    const isParentWithGroups = activeSection === "vast_keuze";
    if (isParentWithGroups && subNavItems.length > 0) {
      const curIdx = subNavItems.findIndex((s) => s.id === activeSubId);
      const nextGroup = curIdx >= 0 && curIdx < subNavItems.length - 1 ? subNavItems[curIdx + 1]! : null;
      if (nextGroup) {
        // Not the last group yet -> step to the next group within this section.
        return {
          label: `Naar: ${nextGroup.label}`,
          onClick: () => setActiveSubId(nextGroup.id),
          isConfirm: false,
        };
      }
      // Last group of this section -> advance to the next section.
      return { label: nextSectionLabel, onClick: goToNextSection, isConfirm: false };
    }

    // Flat sections (Deskundigheidsbevordering, Extra maatwerk): advance to the next section.
    return { label: nextSectionLabel, onClick: goToNextSection, isConfirm: false };
  }, [activeSection, subNavItems, activeSubId, setActiveSubId, navigate, schoolId]);

  // Gate: the programme flow only opens once the school has DELIVERED its gegevens at least
  // once — KUVO plans the programme on those groups/leerlingaantallen, so we need them first.
  // Decided from the cached/inlined bootstrap so it is correct on the first paint (no flash).
  // Pending gegevens-changes do NOT re-lock (the school has already started); that case is the
  // "lever uw wijzigingen opnieuw aan"-nudge elsewhere.
  if (bootstrap && bootstrap.flows.schoolgegevens.kind === "never_submitted") {
    return (
      <div className="min-h-screen bg-background">
        <PortalNav schoolName={bootstrap.school.schoolName ?? schoolInfo?.schoolName} schoolId={schoolId} saveLabel={null} saveError={false} />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-lg py-16 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10">
              <LockIcon className="text-primary" size={20} />
            </div>
            <h1 className="mt-4 text-xl font-semibold tracking-tight text-foreground">Eerst de gegevens, dan het programma</h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Zodra de schoolgegevens, groepen en beschikbaarheid zijn aangeleverd, kiest u hier het programma. KUVO maakt de planning op basis van die gegevens, dus we beginnen daar. Geen haast, u kunt het rustig stap voor stap doorlopen.
            </p>
            <Button className="mt-6" onClick={() => navigate(`/school/${schoolId}/schoolgegevens`)}>
              Verder met de gegevens
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Loading state — keep the header/nav visible (no full-screen reset); skeleton body.
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PortalNav schoolName={bootstrap?.school.schoolName ?? schoolInfo?.schoolName} schoolId={schoolId} saveLabel={null} saveError={false} />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="mb-6 space-y-2">
              <div className="h-6 w-48 rounded bg-muted" />
              <div className="h-4 w-96 max-w-full rounded bg-muted" />
            </div>
            <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12">
              <div className="space-y-4">
                {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-5 w-32 rounded bg-muted" />)}
              </div>
              <div className="h-64 rounded-lg bg-muted" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="mx-auto max-w-md px-6 text-center">
          <h1 className="text-lg font-bold text-foreground">Kan de gegevens niet laden</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Controleer uw internetverbinding en probeer het opnieuw.
          </p>
          <Button size="lg" className="mt-4" onClick={() => { setLoading(true); refreshSections(); }}>
            Opnieuw proberen
          </Button>
        </div>
      </div>
    );
  }

  // Auto-save feedback lives in the SaveToast (bottom-right), which debounces its own spinner.

  return (
    <ProgrammaContext.Provider
      value={{ schoolId, schoolInfo, sections, activeSection, saveState, lastSavedAt, setSaveState, setLastSavedAt, refreshSections, completeSection, primaryAction, subNavItems, setSubNavItems, activeSubId, setActiveSubId }}
    >
      <div className="min-h-screen bg-background">
        <PortalNav
          schoolName={schoolInfo?.schoolName ?? bootstrap?.school.schoolName}
          schoolId={schoolId}
          saveLabel={null}
          saveError={false}
        />
        <SaveToast saveState={saveState} lastSavedAt={lastSavedAt} />

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <PortalHeader title="Programmaselectie" subtitle="Geef uw programmaselectie en voorkeursperiodes per groep aan." />

          <div className="grid gap-8 max-lg:grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12">
            {/* Sidebar stepper */}
            <nav className="lg:sticky lg:top-8 lg:self-start" aria-label="Stappen">
              <ol className="space-y-0">
                {sections.map((section, index) => {
                  const code = section.code as ProgrammaSectionCode;
                  const isActive = code === activeSection;
                  const canOpen = canOpenProgrammaSection(sections, code);
                  const isLast = index === sections.length - 1;
                  // Only Vast- & keuzeprogramma still expands into a per-group sub-nav. TechnoHub is
                  // a single workshop-centric page now, so it renders like the other flat sections.
                  const isParentWithGroups = code === "vast_keuze";
                  const hasSub = isParentWithGroups && isActive && subNavItems.length > 0;
                  // Controleren uses the flow-level state; every other section its own section-level
                  // deliveryState (both stay flow/section-level, per spec). The Vast- & keuze SECTION
                  // also honours the optimistic "changed" hint so its badge reacts the instant a save
                  // starts; the refreshed /progress then reconciles section.deliveryState. (Only when
                  // the section was already delivered, so we never override its no_version/blue state.)
                  const ds: DeliveryState | undefined =
                    code === "programma_controleren" ? controlerenDelivery
                      : optimisticSection === code && section.deliveryState === "delivered" ? "changed"
                        : section.deliveryState;

                  // PARENT ICON. For the two per-group sections (Vast- & keuze, TechnoHub) the parent
                  // rolls up its GROUPS, not the coarse server "done" (which lights up on any single
                  // selection):
                  //   - ACTIVE  -> roll up live from subNavItems: every group resolved -> check,
                  //               some resolved/partial -> (o) dot, none -> ring.
                  //   - INACTIVE -> there are no subNavItems, so use the server signal
                  //               (allGroupsResolved / anyGroupStarted) computed from the same shared
                  //               programme rules. This stays correct AND never blanks when another
                  //               section is active.
                  // All other sections (Deskundigheidsbevordering, Extra maatwerk, Controleren) keep
                  // the persisted server status (status done = complete).
                  let complete: boolean;
                  let started: boolean;
                  if (isParentWithGroups) {
                    if (hasSub) {
                      complete = subNavItems.every((s) => s.resolved);
                      started = subNavItems.some((s) => s.resolved || s.partial);
                    } else {
                      complete = section.allGroupsResolved ?? false;
                      started = section.anyGroupStarted ?? false;
                    }
                  } else if (code === "programma_controleren") {
                    // A delivered programma can still be INCOMPLETE: a group with vast aanbod that
                    // was never decided (geen vaste voorstelling én geen opt-out) leaves the flow
                    // delivered-but-incomplete (e.g. a school that submitted before that rule was
                    // added). getProgrammeResolution gates the submit + the Controleren blocking list
                    // on exactly this; the stepper must match it, so the Controleren node is GREEN
                    // only when delivered AND the programma is COMPLETE. Completeness uses the same
                    // authoritative signal the Vast- & keuze parent uses:
                    // programmaSectionResolution.vast_keuze.allGroupsResolved (exposed on the
                    // vast_keuze section meta, computed by getProgrammeSectionResolution which checks
                    // vast per group). "Delivered" here means the flow has EVER been aangeleverd: ds
                    // 'delivered'/'changed' OR the programme_review step done (after a per-section
                    // revert ds may read delivered while the review step status lags).
                    const programmaComplete = sections.find((s) => s.code === "vast_keuze")?.allGroupsResolved ?? false;
                    const delivered = ds === "delivered" || ds === "changed" || isCompleted(section.status);
                    // GREEN only when delivered AND complete (and not pending-changes: a 'changed' ds
                    // still reads as a dot). When delivered-but-incomplete we DROP `complete` so
                    // stepIconKind yields a (o) dot (started) for attention; before the first
                    // aanlevering nothing is delivered, so it stays a ring.
                    complete = ds === "delivered" && programmaComplete;
                    started = delivered || section.status === "in_progress";
                  } else {
                    complete = isCompleted(section.status);
                    started = section.status === "in_progress";
                  }
                  const kind = stepIconKind(complete, ds, started);
                  const lineTone = kind === "green" ? "bg-emerald-600/40" : kind === "ring" ? "bg-border" : "bg-primary/40";
                  // The clickable/complete label still uses the persisted status for navigability.
                  const navDone = isCompleted(section.status);

                  return (
                    <Fragment key={code}>
                      <li className={`relative flex items-center gap-3 ${hasSub ? "pb-3" : "pb-7"} last:pb-0`}>
                        <div className="relative z-10 shrink-0">
                          {!isLast && (hasSub
                            ? <div className="absolute left-[11.5px] top-full h-3 w-px bg-border" aria-hidden="true" />
                            : <div className={`absolute left-[11.5px] top-full h-7 w-px ${lineTone}`} aria-hidden="true" />)}
                          {/* Dim non-active circles so the active parent stands out (subtle). */}
                          <StepCircle kind={kind} active={isActive} canOpen={canOpen} dim={!isActive} changed={ds === "changed"} />
                        </div>
                        {/* The active section keeps full emphasis; the rest are subtly dimmed. */}
                        <button
                          type="button"
                          aria-current={isActive ? "step" : undefined}
                          className={`flex flex-col items-start gap-1 text-left text-sm outline-none transition-colors focus-visible:underline ${isActive ? "font-semibold text-foreground" : navDone || canOpen ? "cursor-pointer text-muted-foreground/70 hover:text-foreground" : "text-muted-foreground/40"}`}
                          onClick={canOpen ? () => handleOpenSection(code) : undefined}
                        >
                          <span>{SECTION_SHORT_TITLES[code]}</span>
                          {/* Disambiguate the (o) dot: a gewijzigd parent gets a short label so the
                              dot never has to carry the meaning alone (the tooltip does the rest).
                              The pill sits on its own line below the label so a long label keeps
                              its line instead of wrapping mid-word. */}
                          {kind === "dot" && ds === "changed" && (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">Gewijzigd</span>
                          )}
                        </button>
                      </li>
                      {hasSub && subNavItems.map((sub, si) => {
                        const subActive = sub.id === activeSubId;
                        // PER-GROUP delivery state (BUG B fix). Each group's badge reflects ONLY its
                        // OWN change vs the last delivered version (from /progress' programmaGroupDelivery,
                        // phase 1), NOT the whole-section deliveryState — so changing one group flags only
                        // that group. The optimistic "changed" hint takes precedence the instant a save
                        // starts; the refreshed /progress then reconciles it (a group whose edit reverted to
                        // the submitted value flips back to "delivered"). Fall back to the section ds only
                        // when the server has not reported this group yet (e.g. a brand-new group).
                        // The optimistic "changed" hint only applies to a group that was already
                        // DELIVERED (a real change since aanlevering). For a never-submitted group
                        // (no_version) an edit is just filling in, not a change, so we never flash
                        // "Gewijzigd" before the first aanlevering.
                        const subDs: DeliveryState | undefined =
                          optimisticGroups.has(sub.id) && groupDelivery[String(sub.id)] === "delivered"
                            ? "changed"
                            : (groupDelivery[String(sub.id)] ?? ds);
                        // Same green/blue/dot/ring rule as the parent. A row's completeness is its
                        // own `resolved`; combined with the per-group delivery state: resolved +
                        // delivered = green; resolved + not-yet/again aangeleverd = BLUE check;
                        // 'changed' (gewijzigd) = dot; deels ingevuld (partial) = (o) dot; nothing
                        // started = empty ring. `partial` feeds stepIconKind's `started` flag.
                        const subKind = stepIconKind(sub.resolved, subDs, sub.partial);
                        return (
                          <li key={`${code}-${sub.id}`} className={`relative flex items-center gap-3 pl-6 ${si === subNavItems.length - 1 ? "pb-5" : "pb-4"}`}>
                            {/* Folder-tree trunk: vertical line on the parent-circle centre (x=12), full row height. */}
                            <div className="absolute left-[11.5px] top-0 bottom-0 w-px bg-border" aria-hidden="true" />
                            {/* Branch into this circle at its vertical centre, stopping exactly at the circle's left edge. */}
                            <div className="absolute left-[11.5px] top-[11.5px] h-px w-[12.5px] bg-border" aria-hidden="true" />
                            <span className="relative z-10 flex w-6 shrink-0 justify-center">
                              {/* Dim non-active sub-items so the active group stands out. */}
                              <StepCircle kind={subKind} active={subActive} canOpen={subActive} dim={!subActive} changed={subDs === "changed"} />
                            </span>
                            <button
                              type="button"
                              onClick={() => setActiveSubId(sub.id)}
                              title={sub.label}
                              className={`flex min-w-0 flex-col items-start gap-1 text-left text-sm outline-none transition-colors focus-visible:underline ${subActive ? "font-semibold text-foreground" : "cursor-pointer text-muted-foreground/70 hover:text-foreground"}`}
                            >
                              <span className="max-w-[10rem] truncate">{sub.label}</span>
                              {/* A gewijzigde groep is also marked, so the dot is never the only signal.
                                  The pill sits on its own line below the label so a long group name
                                  keeps its line instead of wrapping mid-word. */}
                              {subKind === "dot" && subDs === "changed" && (
                                <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">Gewijzigd</span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </ol>

              {/* Full programme explanation (PDF), opened in a wide zoom/pan dialog. The browser's
                  native PDF viewer provides zoom + scroll/pan; #view=FitH opens fit-to-width at the top. */}
              <div className="mt-6 lg:max-w-[220px]">
                <Dialog>
                  <DialogTrigger
                    render={
                      <button
                        type="button"
                        className="group flex w-full cursor-pointer items-center gap-2.5 rounded-xl border bg-card px-3.5 py-3 text-left shadow-xs transition-all hover:border-primary/40 hover:shadow-sm"
                      >
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          <FileTextIcon size={17} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-semibold leading-snug text-foreground break-words">Programmatoelichting</span>
                          <span className="block text-xs text-muted-foreground">Bekijk de PDF</span>
                        </span>
                      </button>
                    }
                  />
                  <DialogPopup className="w-[94vw] max-w-6xl">
                    <DialogHeader>
                      <DialogTitle>Programmatoelichting 2026-2027</DialogTitle>
                    </DialogHeader>
                    <div className="px-6 pb-6">
                      <iframe
                        src="/programmatoelichting-2026-2027.pdf#view=FitH"
                        title="KUVO Programmatoelichting PO 2026-2027"
                        className="h-[78vh] w-full rounded-md border bg-muted"
                      />
                    </div>
                  </DialogPopup>
                </Dialog>
              </div>
            </nav>

            {/* Content */}
            <section className="min-w-0">
              {activeSection && (
                <>
                  <header className="mb-5">
                    <h2 className="text-base font-semibold text-foreground">
                      {SECTION_TITLES[activeSection]}
                    </h2>
                  </header>
                  {/* Staff read-only preview disables all inputs/buttons in the section body
                      (the server also blocks every write). */}
                  <fieldset disabled={isPreview} className="m-0 min-w-0 border-0 p-0">
                    {renderSection(activeSection, schoolId)}
                  </fieldset>
                </>
              )}
            </section>
          </div>
        </main>
      </div>
    </ProgrammaContext.Provider>
  );
}
